import {
  CapacitorBrowser,
  CapacitorClipboard,
  CapacitorDirectory,
  CapacitorFilesystem,
  CapacitorShare
} from 'boot/inject-capacitor'
import ShareModal from 'components/ShareModal.vue'
import Filter from 'src/etc/qutils/filter'
import { api } from 'boot/axios'
import { truncate } from 'src/etc/utils'
import { Advert } from 'src/models/advert'
import { copyToClipboard, Loading, QSpinnerGears } from 'quasar'
import ShareModalLinkOrImage from 'components/ShareModalLinkOrImage.vue'
import { useTracking } from 'src/etc/tracking'


async function shareAdvertImage(advert: Advert): Promise<string | File> {
  const response = await api.get(`adverts/${advert.uuid}/share-image/`, { responseType: 'blob' })
  const blob = await response.data

  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('FileReader error'))
    reader.onload = () => {
      const dataUrl = reader.result as string
      resolve(dataUrl.split(',')[1])
    }
    reader.readAsDataURL(blob)
  })

  const name = `share-${Date.now()}.png`

  if (CapacitorFilesystem && CapacitorDirectory) {
    const { uri } = await CapacitorFilesystem.writeFile({
      path: name,
      data: base64,
      directory: CapacitorDirectory.Cache
    })
    return uri
  }
  return new File([blob], 'shared-image.png', { type: blob.type })
}


export function shareAdvertOnMobile(advert: Advert, text: string, asImage = false) {
  return new Promise(async (resolve, reject) => {
    const shareOptions: any = {
      title: 'Gefunden auf CamperTrader.de',
      text: text,
      url: advert.seo_link,
      dialogTitle: 'Inserat teilen'
    }

    if (asImage && advert.cover_image?.image) {
      Loading.show({
        message: 'Bild wird erstellt',
        spinner: QSpinnerGears
      })
      const imageUri = await shareAdvertImage(advert)
      shareOptions.files = [imageUri]
      Loading.hide()
    }

    console.debug('shareOptions', JSON.stringify(shareOptions))
    if (CapacitorShare) {
      const { value } = await CapacitorShare.canShare()
      if (value) {
        try {
          await CapacitorShare.share(shareOptions)
          resolve(shareOptions)
        } catch (error) {
          reject(error)
        }
      } else {
        console.error('Sharing not supported on this platform')
        reject('Not supported')
      }
    } else if (typeof window !== 'undefined' && window.navigator) {
      // Fallback for web
      try {
        await window.navigator.share(shareOptions)
        resolve(shareOptions)
      } catch (err) {
        reject(err)
      }
    } else {
      console.error('Sharing not supported on this platform')
      reject('Not supported')
    }
  })
}

export function navigatorCanShare() {
  return typeof window !== 'undefined' && window.navigator && window.navigator.canShare
}

export async function navigatorCanShareMedia(): Promise<boolean> {
  // Check Capacitor Share API first (for native mobile)
  // Check Web Share API with files capability

  if (typeof navigator !== 'undefined' && navigator.share as any) {
    try {
      // Create a dummy file object to test if sharing files is supported
      const dummyFile = new File([''], 'test.png', { type: 'image/png' })
      return navigator.canShare && navigator.canShare({ files: [dummyFile] })
    } catch (error) {
      console.error('Error checking navigator.canShare capabilities:', error)
      return false
    }
  }

  return false
}

async function _share($q, advert, text, payload: 'link' | 'image') {
  const tracker = useTracking()

  return new Promise(async (resolve, reject) => {
    try {
      if ($q.platform.is.nativeMobile && CapacitorClipboard) {
        await CapacitorClipboard.write({
          url: advert.seo_link
        })
      } else {
        await copyToClipboard(advert.seo_link)
      }
    } catch (error) {
      console.error('Error copying URL to clipboard:', error)
    }

    shareAdvertOnMobile(advert, text, payload === 'image').then(() => {
      tracker.trackLinkShare('advert', payload)
      resolve({})
    }).catch(err => {
      if (!err?.message.includes('cancel')) {
        console.error('Error sharing advert:', err)
        $q.notify({
          type: 'negative',
          message: 'Fehler beim Teilen aufgetreten. Bitte versuche es noch einmal oder kontaktiere uns.'
        })
        reject(err)
      } else {
        console.info('Canceled:', err)
        resolve(true)
      }
    })
  })
}


export async function shareAdvert($q, advert: Advert, direct_share: 'link' | 'image' | undefined = undefined) {
  const tracker = useTracking()

  return new Promise(async (resolve) => {
    const formattedPrice = Filter.currency({
      value: advert.price,
      maximumFractionDigits: 0
    })
    const text = `${advert?.seo_title} • ${formattedPrice}`
    let capacitorCanShare = false
    if (CapacitorShare) {
      const { value } = await CapacitorShare.canShare()
      capacitorCanShare = value
    }
    if (capacitorCanShare || navigatorCanShare()) {
      if (direct_share) {
        try {
          await _share($q, advert, text, direct_share)
        } catch (e) {
          console.error('Error sharing:', e)
        } finally {
          resolve(true)
        }
      } else if (!await navigatorCanShareMedia()) {
        // if navigator can not share media fallback to link
        try {
          await _share($q, advert, text, 'link')
        } catch (e) {
          console.error('Error sharing:', e)
        } finally {
          resolve(true)
        }
      } else {
        // ask for link or media
        $q.dialog({
          component: ShareModalLinkOrImage,
          componentProps: {
            advert: advert
          }
        }).onOk(async (payload: 'image' | 'link') => {
          try {
            await _share($q, advert, text, payload)
          } finally {
            resolve(true)
          }
        }).onDismiss(() => {
          resolve(true)
        })
      }
    } else {
      // if not native or sharing image failed, show the share modal
      $q.dialog({
        component: ShareModal,

        // props forwarded to your custom component
        componentProps: {
          url: advert?.seo_link,
          text: text,
          mtmSource: 'AdvertDetail'
        }
      }).onDismiss(() => {
        resolve(true)
      }).onOk(() => {
        tracker.trackLinkShare('advert', 'link')
      })
    }
  })

}


export async function shareItem($q, item) {
  const tracker = useTracking()
  let capacitorCanShare = false
  if (CapacitorShare) {
    const { value } = await CapacitorShare.canShare()
    capacitorCanShare = value
  }
  if (capacitorCanShare || navigatorCanShare()) {
    const shareOptions = {
      title: 'Gefunden auf CamperTrader.de',
      text: truncate(item.description_clean, 200),
      url: item.seo_link,
      dialogTitle: 'Inserat teilen'
    }
    if (CapacitorShare) {
      try {
        await CapacitorShare.share(shareOptions)
      } catch (error: any) {
        console.warn('Error sharing:', error)
        return
      }
    } else if (typeof window !== 'undefined' && window.navigator) {
      // Fallback for web
      try {
        await window.navigator.share(shareOptions)
      } catch (error: any) {
        console.warn('Error sharing:', error)
        return
      }
    } else {
      console.error('Sharing not supported on this platform')
    }
    tracker.trackLinkShare('item', 'link')
    return
  }


  // if not native or sharing image failed, show the share modal
  $q.dialog({
    component: ShareModal,

    // props forwarded to your custom component
    componentProps: {
      url: item?.seo_link,
      text: truncate(item.description_clean, 200),
      mtmSource: 'ItemDetail'
    }
  }).onOk(() => {
    tracker.trackLinkShare('item', 'link')
  })
}


export function clickSaleSign($q, advert) {
  const url = new URL(`adverts/${advert.uuid}/sale-sign/`, process.env.API_BASE)

  if ($q.platform.is.nativeMobile) {
    CapacitorBrowser.open({ url: url.href })
  } else {
    const link = document.createElement('a')
    link.href = url.href
    link.target = '_blank'
    link.download = 'verkaufsschild.pdf' // Specify the file name
    link.click()
  }
}
